
import { useEffect, useState } from 'react';
import {
  getBilling,
  getBillingOrders,
  createBillingCheckout,
  resumeBillingOrder,
  verifyBillingOrder,
} from '../api.js';
import './BillingPanel.css';

const money = (value) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const dateText = (value) =>
  value ? new Date(value).toLocaleDateString('ru-RU') : '—';

const statuses = {
  pending: 'Ожидает оплаты',
  succeeded: 'Оплачен',
  canceled: 'Отменён',
  refunded: 'Возвращён',
};

function safePaymentLink(value) {
  if (typeof value !== 'string' || !value) return null;

  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();

    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      !['yookassa.ru', 'yoomoney.ru'].some(
        (name) => host === name || host.endsWith(`.${name}`)
      )
    ) {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
}

export default function BillingPanel() {
  const [subscription, setSubscription] = useState(null);
  const [orders, setOrders] = useState([]);
  const [channels, setChannels] = useState('1');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [paymentLink, setPaymentLink] = useState('');

  async function refresh() {
    const [state, history] = await Promise.all([
      getBilling(),
      getBillingOrders(),
    ]);

    setSubscription(state);
    setOrders(Array.isArray(history) ? history : []);
    return state;
  }

  useEffect(() => {
    let active = true;

    refresh()
      .catch(() => {
        if (active) setError('Не удалось загрузить подписку.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    let checking = false;

    const onReturn = async () => {
      if (
        document.visibilityState !== 'visible' ||
        checking
      ) return;

      checking = true;

      try {
        const history = await getBillingOrders();
        const unfinished = Array.isArray(history)
          ? history.find((item) => item.status === 'pending')
          : null;

        if (unfinished) {
          await verifyBillingOrder(unfinished.id);
        }

        await refresh();
      } catch {
        await refresh().catch(() => {});
      } finally {
        checking = false;
      }
    };

    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);

    const timer = window.setInterval(onReturn, 30000);

    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, []);

  const pending = orders.find((order) => order.status === 'pending');

  const legacy =
    subscription?.status === 'active' &&
    !subscription?.paid_until;

  const expires = subscription?.paid_until
    ? Date.parse(subscription.paid_until)
    : 0;

  const activated =
    subscription?.status === 'active' &&
    (legacy || expires > Date.now());

  const available =
    subscription?.payment_available === true && !legacy;

  const count = pending
    ? Number(pending.channels)
    : Number(channels);

  const valid =
    Number.isInteger(count) && count >= 1 && count <= 1000;

  const unit = Number(
    subscription?.price_per_channel_rub_month ?? 3000
  );

  const total = pending
    ? Number(pending.total_kopeks) / 100
    : valid ? count * unit : 0;

  async function pay(event) {
    event.preventDefault();

    if (busy || !available || !valid) return;
    if (!pending && !email.trim()) return;

    // Окно открывается непосредственно при нажатии пользователя,
    // до асинхронного запроса, иначе браузер блокирует его.
    let paymentWindow = null;

    try {
      paymentWindow = window.open('about:blank', '_blank');

      if (paymentWindow) {
        try {
          paymentWindow.document.title = 'Оплата «Связки»';
          paymentWindow.document.body.textContent =
            'Подготавливаем безопасную оплату…';
          paymentWindow.opener = null;
        } catch {
          // Встроенный браузер может ограничивать доступ к окну.
        }
      }
    } catch {
      paymentWindow = null;
    }

    setBusy(true);
    setError('');
    setNotice('');
    setPaymentLink('');

    try {
      const result = pending
        ? await resumeBillingOrder(pending.id)
        : await createBillingCheckout({
            channels: count,
            receipt_email: email.trim(),
            recurring_consent: false,
          });

      const link = safePaymentLink(result?.confirmation_url);

      if (!link) {
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }

        await refresh();

        if (result?.status === 'canceled') {
          setNotice(
            'Предыдущий платёж отменён. ' +
            'Теперь можно оформить новую оплату.'
          );
        } else if (result?.status === 'succeeded') {
          setNotice(
            'ЮKassa подтвердила платёж. ' +
            'Проверьте состояние подписки.'
          );
        } else {
          setNotice(
            'Предыдущий платёж ещё обрабатывается ЮKassa. ' +
            'Новой оплаты пока не требуется. ' +
            'Статус проверяется автоматически.'
          );
        }

        return;
      }

      setPaymentLink(link);

      let opened = false;

      if (paymentWindow && !paymentWindow.closed) {
        try {
          paymentWindow.location.replace(link);
          opened = true;
        } catch {
          try { paymentWindow.close(); } catch {}
        }
      }

      setNotice(
        opened
          ? 'Оплата открыта в новой вкладке. ' +
            'После оплаты вернитесь сюда.'
          : 'Браузер не открыл страницу автоматически. ' +
            'Нажмите ссылку оплаты ниже.'
      );

      await refresh();
    } catch (exc) {
      try {
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
      } catch {}

      setError(
        exc?.message ||
        'Не удалось открыть оплату. Повторите попытку.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function checkPayment() {
    if (busy) return;

    setBusy(true);
    setError('');
    setNotice('');

    try {
      const outcome = pending
        ? await verifyBillingOrder(pending.id)
        : null;

      const state = await refresh();

      if (outcome?.result === 'canceled') {
        setNotice(
          'Предыдущий платёж отменён. ' +
          'Введите почту для чека и оформите новую оплату.'
        );
        return;
      }
      const end = state?.paid_until
        ? Date.parse(state.paid_until)
        : 0;

      setNotice(
        state?.status === 'active' &&
        (end > Date.now() || !state.paid_until)
          ? 'Оплата подтверждена. Подписка активна.'
          : 'Оплата пока не подтверждена. ' +
            'Если вы уже заплатили, повторите проверку чуть позже.'
      );
    } catch (exc) {
      setError(
        exc?.message ||
        'Не удалось проверить оплату. Попробуйте ещё раз.'
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section className="sv-pay-shell" aria-live="polite">
        <p>Загружаем информацию о подписке…</p>
      </section>
    );
  }

  return (
    <section className="sv-pay-shell" aria-label="Подписка Связки">
      <div className="sv-pay-heading">
        <div className="sv-pay-eyebrow">СВЯЗКА · ПОДПИСКА</div>
        <h2>
          {activated
            ? 'Ваша подписка'
            : 'Подключите первый канал'}
        </h2>
        <p>
          {activated
            ? 'Управляйте количеством каналов и оплатой в одном месте.'
            : 'Оплатите подписку и подключите Telegram, MAX или WhatsApp к Битрикс24.'}
        </p>
      </div>

      {error && (
        <div className="sv-pay-error" role="alert">{error}</div>
      )}

      {notice && (
        <div className="sv-pay-notice" role="status">{notice}</div>
      )}

      <div className="sv-pay-card">
        <div className="sv-pay-summary">
          <div>
            <span className="sv-pay-label">
              {pending ? 'Заказ ожидает оплаты' : 'Стоимость за месяц'}
            </span>
            <div className="sv-pay-price">{money(total)}</div>
            <p>
              {count} {count === 1 ? 'канал' : 'канал(ов)'}
              {' · '}один месяц
            </p>
          </div>
          <div className="sv-pay-secure">
            <span>Оплата через ЮKassa</span>
            <small>Платёж подтверждается автоматически</small>
          </div>
        </div>

        {pending && (
          <div className="sv-pay-pending">
            Заказ на {money(total)} ожидает решения ЮKassa.
            Если вы прервали оплату, дождитесь проверки статуса.
            Как только отмена подтвердится, можно будет
            оформить новый платёж.
          </div>
        )}

        {legacy ? (
          <div className="sv-pay-legacy">
            Действующая подписка обслуживается по ранее
            согласованным условиям.
          </div>
        ) : (
          <form className="sv-pay-form" onSubmit={pay}>
            <div className="sv-pay-fields">
              <div>
                <label htmlFor="sv-pay-count">Количество каналов</label>
                <input
                  id="sv-pay-count"
                  type="number"
                  min="1"
                  max="1000"
                  step="1"
                  required
                  disabled={Boolean(pending) || busy}
                  value={pending ? String(pending.channels) : channels}
                  onChange={(event) => setChannels(event.target.value)}
                />
              </div>

              {!pending && (
                <div>
                  <label htmlFor="sv-pay-email">Почта для чека</label>
                  <input
                    id="sv-pay-email"
                    type="email"
                    maxLength="254"
                    autoComplete="email"
                    placeholder="mail@example.ru"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              )}
            </div>

            <button
              className="sv-pay-primary"
              type="submit"
              disabled={!available || !valid || busy}
            >
              {busy
                ? 'Подготавливаем оплату…'
                : pending
                  ? `Продолжить оплату — ${money(total)} ↗`
                  : `Перейти к оплате — ${money(total)} ↗`}
            </button>

            {paymentLink && (
              <a
                className="sv-pay-fallback"
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть страницу оплаты в ЮKassa ↗
              </a>
            )}

            {!available && (
              <p className="sv-pay-help">
                Онлайн-оплата сейчас недоступна.
                Повторите попытку позднее.
              </p>
            )}

            <p className="sv-pay-help">
              Оплата выполняется на защищённой странице ЮKassa.
              Автоматическое продление не подключается.
            </p>
          </form>
        )}
      </div>

      <div className="sv-pay-bottom">
        <div className="sv-pay-current">
          <strong>
            {activated ? 'Подписка активна' : 'Подписка не оплачена'}
          </strong>
          <span>
            {activated
              ? `${subscription.channel_limit} канал(ов), действует до ${dateText(subscription.paid_until)}`
              : 'Каналы станут доступны после подтверждения оплаты'}
          </span>
        </div>

        <p className="sv-pay-help">
          Статус оплаты обновляется автоматически.
        </p>
      </div>

      <details className="sv-pay-history">
        <summary>История платежей</summary>

        {orders.length === 0 ? (
          <p>Платежей пока нет.</p>
        ) : (
          <div className="sv-pay-orders">
            {orders.map((order) => (
              <div className="sv-pay-order" key={order.id}>
                <div>
                  <strong>{money(order.total_kopeks / 100)}</strong>
                  <span>
                    {dateText(order.created_at)} ·{' '}
                    {order.channels} канал(ов)
                  </span>
                </div>
                <span>{statuses[order.status] || order.status}</span>
              </div>
            ))}
          </div>
        )}
      </details>
    </section>
  );
}
